import { baseFabricFocusSelector, appNeutralColors, getInnerBorderStyles } from '@ms/acui-common/lib/styling';
import { AppColors, getModernUxExperimentPreference } from '@ms/acui-common/lib/utilities';
import { Variant, AppThemedVariant, AppButtonVariants } from './AppButtonVariants';
import { IButtonStyles, memoizeFunction } from '@fluentui/react';
import { IStyle, mergeStyles, mergeStyleSets, HighContrastSelector } from '@fluentui/react/lib/Styling';

/* Public API */
/**
 * @param variant the desired look of the control that will
 * @param appColors set of app colors to be used in the styles
 * @param disabled boolean that indicates if the control is in a disabled state
 *
 * @returns set of styles matching the variant provided
 */
export const getAppThemedButtonStyles = memoizeFunction((variant: Variant, appColors: AppColors, disabled?: boolean):
  | IButtonStyles
  | undefined => {
  if (variant === Variant.primary || variant === Variant.inverted) {
    updateSharedValues();
    if (disabled) {
      return getDisabledControlStyles(appColors);
    }
    if (variant === Variant.primary) {
      return primaryControlStyles(appColors);
    }
    if (variant === Variant.inverted) {
      return invertedControlStyles(appColors);
    }
  }
  return undefined;
});

/**
 * @param variant the desired look of the control that will
 * @param appColors set of app colors to be used in the styles
 * @param disabled boolean that indicates if the control is in a disabled state
 *
 * @returns set of styles for the split button matching the variant provided
 */
export const getAppThemedSplitButtonStyles = memoizeFunction(
  (variant: Variant, appColors: AppColors, disabled?: boolean): IButtonStyles | undefined => {
    if (variant === Variant.primary || variant === Variant.inverted) {
      updateSharedValues();
      if (disabled) {
        return getDisabledSplitButtonStyles(appColors);
      }
      if (variant === Variant.primary) {
        return primarySplitButtonStyles(appColors);
      }
      if (variant === Variant.inverted) {
        return invertedSplitButtonStyles(appColors);
      }
    }
    return undefined;
  }
);

/**
 * @param variantProps the object that contains the variantProps
 *
 * @returns a casted version of the variantProps for AppThemed variants
 */
export const isAppThemedVariant = memoizeFunction(
  (variantProps?: AppButtonVariants): variantProps is AppThemedVariant => {
    return (
      variantProps !== undefined &&
      (variantProps.variantLook === Variant.primary || variantProps.variantLook === Variant.inverted) &&
      (variantProps as AppThemedVariant).appColors !== undefined
    );
  }
);

/* Helper private methods and members */

/* Constants: borders */
let borderRadius: number = 0;
let borderRest: string = '';
let borderHovered: string = '';
let borderPressed: string = '';
let borderSelected: string = '';
const borderFocused: string = appNeutralColors.black;
const borderDisabled: string = appNeutralColors.gray50;
const borderThicknessStandard: string = '1';
const borderThicknessFocused: string = '2';

/* Variables: app palette
 * These are set at runtime because the appColors are required
 */
let appPaletteRest: string = '';
let appPaletteHovered: string = '';
let appPalettePressed: string = '';
let appPaletteSelected: string = '';

/* Constants: neutrals palette */
const neutralRest: string = appNeutralColors.white;
let neutralHovered: string = '';
let neutralPressed: string = '';
let neutralSelected: string = '';
const neutralDisabled: string = appNeutralColors.gray20;
const neutralDisabledContent: string = appNeutralColors.gray70;

/* Constants: class names */
export const appThemedButtonClassNames = {
  label: 'themedBtn-label',
  icon: 'themedBtn-icon',
  splitButtonMainControl: 'themedBtn-splitBtn-mainControl',
  splitButtonMenuControl: 'themedBtn-splitBtn-menuControl',
};

/* Constants: class selectors */
const appThemedButtonLabelSelector: string = `.${appThemedButtonClassNames.label}`;
const appThemedButtonIconSelector: string = `.${appThemedButtonClassNames.icon}`;
const appThemedButtonIconPathSelector: string = `.${appThemedButtonClassNames.icon} path`;
const appThemedSplitButtonMainControlSelector: string = `.${appThemedButtonClassNames.splitButtonMainControl}`;
const appThemedSplitButtonMenuControlSelector: string = `.${appThemedButtonClassNames.splitButtonMenuControl}`;

/**
 * @param backgroundColor color of the control background
 * @param contentColor color of the content rendered inside of the control, includes labels and icons
 * @param borderColor color of the border surrounding the main control
 *
 * @returns a set of styles that uses the provided colors
 */
const applyControlStyles = memoizeFunction(
  (backgroundColor?: string, contentColor?: string, borderColor?: string): IStyle => {
    const borderStandardStyles: IStyle = borderColor
      ? {
          border: `${borderThicknessStandard}px solid ${borderColor}`,
          borderRadius,
        }
      : undefined;

    // generate styles set
    return mergeStyles(borderStandardStyles, {
      backgroundColor,
      selectors: {
        [appThemedButtonLabelSelector]: {
          color: contentColor,
        },
        [appThemedButtonIconPathSelector]: {
          // icon path fill
          fill: contentColor,
        },
        [appThemedButtonIconSelector]: {
          border: 'none',
          background: 'transparent',
        },
      },
    });
  }
);

/* Private helpers: generic buttons */

/**
 * Updates values used across the styles based on app settings.
 * Ongoing experiments can use this function to set values that impact
 * shared styles.
 */
const updateSharedValues = (): void => {
  const modernUxEnabled = getModernUxExperimentPreference();
  borderRadius = modernUxEnabled ? 4 : 2;
  if (modernUxEnabled) {
    neutralHovered = '#F5F5F5';
    neutralPressed = '#E0E0E0';
    neutralSelected = '#EBEBEB';
    borderRest = '#D1D1D1';
    borderHovered = '#D1D1D1';
    borderPressed = '#D1D1D1';
    borderSelected = '#D1D1D1';
  } else {
    neutralHovered = appNeutralColors.gray30; // EDEBE9
    neutralPressed = appNeutralColors.gray50; // D2D0CE
    neutralSelected = appNeutralColors.gray40; // E1DFDD
    borderRest = appNeutralColors.gray60; // C8C6C4
    borderHovered = appNeutralColors.gray60; // C8C6C4
    borderPressed = appNeutralColors.gray60; // C8C6C4
    borderSelected = appNeutralColors.gray60; // C8C6C4
  }
};

/**
 * Set of styles required to set the focused borders
 * around controls for WCAG compliance.
 */
const applyFocusedBorderStyles = (): IStyle => {
  return {
    selectors: {
      ':after': mergeStyles(
        getInnerBorderStyles({
          width: `${borderThicknessFocused}px`,
          color: borderFocused,
          gap: -1,
        }),
        { borderRadius }
      ),
    },
  };
};

/* State combinations: high contrast/forced colors */
const highContrastStyles: IButtonStyles = {
  root: [
    {
      selectors: {
        [HighContrastSelector]: applyControlStyles('ButtonFace', 'ButtonText', 'ButtonText'),
        [baseFabricFocusSelector]: {
          selectors: {
            [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
          },
        },
      },
    },
  ],
  rootHovered: {
    selectors: {
      [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
    },
  },
  rootPressed: {
    selectors: {
      [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
    },
  },
  // toggle controls
  rootChecked: {
    selectors: {
      [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
    },
  },
  rootCheckedHovered: {
    selectors: {
      [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
    },
  },
  rootCheckedPressed: {
    selectors: {
      [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
    },
  },
  // flyout controls
  rootExpanded: {
    selectors: {
      [HighContrastSelector]: [
        applyControlStyles('Highlight', 'HighlightText', undefined),
        {
          selectors: {
            // no equivalent to splitButtonMenuButtonHovered
            [':hover']: applyControlStyles('Highlight', 'HighlightText', undefined),
            // no equivalent to splitButtonMenuButtonPressed
            [':active']: applyControlStyles('Highlight', 'HighlightText', undefined),
          },
        },
      ],
    },
  },
  rootExpandedHovered: {
    selectors: {
      [HighContrastSelector]: [
        applyControlStyles('Highlight', 'HighlightText', undefined),
        {
          selectors: {
            // no equivalent to splitButtonMenuButtonHovered
            [':hover']: applyControlStyles('Highlight', 'HighlightText', undefined),
            // no equivalent to splitButtonMenuButtonPressed
            [':active']: applyControlStyles('Highlight', 'HighlightText', undefined),
          },
        },
      ],
    },
  },
};

/* State combinations: primary */
const primaryControlStyles = memoizeFunction(
  (appColors: AppColors): IButtonStyles => {
    // set app colors to state variables
    appPaletteRest = appColors.primary;
    appPaletteHovered = appColors.shade10;
    appPalettePressed = appColors.shade30;
    appPaletteSelected = appColors.shade20;
    return mergeStyleSets(
      {
        // all controls
        label: appThemedButtonClassNames.label,
        icon: appThemedButtonClassNames.icon,
        menuIcon: appThemedButtonClassNames.icon,
        root: [
          applyControlStyles(appPaletteRest, neutralRest, appPaletteRest),
          {
            selectors: {
              [baseFabricFocusSelector]: applyFocusedBorderStyles(),
            },
          },
          { position: 'relative' },
        ],
        rootFocused: applyFocusedBorderStyles(),
        rootHovered: applyControlStyles(appPaletteHovered, neutralRest, appPaletteHovered),
        rootPressed: applyControlStyles(appPalettePressed, neutralRest, appPalettePressed),
        // toggle controls
        rootChecked: applyControlStyles(appPaletteSelected, neutralRest, appPaletteSelected),
        rootCheckedHovered: applyControlStyles(appPaletteHovered, neutralRest, appPaletteHovered),
        rootCheckedPressed: applyControlStyles(appPalettePressed, neutralRest, appPalettePressed),
        // flyout controls
        rootExpanded: [
          applyControlStyles(appPaletteSelected, neutralRest, appPaletteSelected),
          {
            selectors: {
              // no equivalent to rootExpandedHoveredPressed
              [':active']: applyControlStyles(appPalettePressed, neutralRest, appPalettePressed),
            },
          },
        ],
        rootExpandedHovered: [
          applyControlStyles(appPaletteHovered, neutralRest, appPaletteHovered),
          {
            selectors: {
              // no equivalent to rootExpandedHoveredPressed
              [':active']: applyControlStyles(appPalettePressed, neutralRest, appPalettePressed),
            },
          },
        ],
      },
      highContrastStyles
    );
  }
);

/* State combinations: inverted */
const invertedControlStyles = memoizeFunction(
  (appColors: AppColors): IButtonStyles => {
    // set app colors to state variables
    const modernUxEnabled = getModernUxExperimentPreference();
    if (modernUxEnabled) {
      appPaletteRest = appColors.primary;
      appPaletteHovered = appColors.primary;
      appPalettePressed = appColors.primary;
      appPaletteSelected = appColors.primary;
    } else {
      appPaletteRest = appColors.primary;
      appPaletteHovered = appColors.shade10;
      appPalettePressed = appColors.shade30;
      appPaletteSelected = appColors.shade20;
    }
    return mergeStyleSets(
      {
        // all controls
        label: appThemedButtonClassNames.label,
        icon: appThemedButtonClassNames.icon,
        menuIcon: appThemedButtonClassNames.icon,
        root: [
          applyControlStyles(neutralRest, appPaletteRest, borderRest),
          {
            selectors: {
              [baseFabricFocusSelector]: applyFocusedBorderStyles(),
            },
          },
          { position: 'relative' },
        ],
        rootFocused: applyFocusedBorderStyles(),
        rootHovered: applyControlStyles(neutralHovered, appPaletteHovered, borderHovered),
        rootPressed: applyControlStyles(neutralPressed, appPalettePressed, borderPressed),
        // toggle controls
        rootChecked: applyControlStyles(neutralSelected, appPaletteSelected, borderSelected),
        rootCheckedHovered: applyControlStyles(neutralHovered, appPaletteHovered, borderHovered),
        rootCheckedPressed: applyControlStyles(neutralPressed, appPalettePressed, borderPressed),
        // flyout controls
        rootExpanded: [
          applyControlStyles(neutralSelected, appPaletteSelected, borderSelected),
          {
            selectors: {
              // no equivalent to rootExpandedHoveredPressed
              [':active']: applyControlStyles(neutralPressed, appPalettePressed, borderPressed),
            },
          },
        ],
        rootExpandedHovered: [
          applyControlStyles(neutralHovered, appPaletteHovered, borderHovered),
          {
            selectors: {
              // no equivalent to rootExpandedHoveredPressed
              [':active']: applyControlStyles(neutralPressed, appPalettePressed, borderPressed),
            },
          },
        ],
      },
      highContrastStyles
    );
  }
);

/* State combinations: disabled */
const getDisabledControlStyles = memoizeFunction(
  (appColors: AppColors): IButtonStyles => {
    const modernUxEnabled = getModernUxExperimentPreference();
    const textColor = modernUxEnabled ? appColors.primary : neutralDisabledContent;
    const backgroundColor = modernUxEnabled ? appNeutralColors.white : neutralDisabled;
    const borderColor = modernUxEnabled ? '#D1D1D1' : borderDisabled;
    return {
      // all controls
      label: appThemedButtonClassNames.label,
      icon: appThemedButtonClassNames.icon,
      menuIcon: appThemedButtonClassNames.icon,
      rootDisabled: [
        applyControlStyles(backgroundColor, textColor, borderColor),
        { position: 'relative' },
        {
          selectors: {
            [baseFabricFocusSelector]: [
              applyFocusedBorderStyles(),
              {
                selectors: {
                  [HighContrastSelector]: applyControlStyles('Highlight', 'GrayText', undefined),
                },
              },
            ],
            [HighContrastSelector]: applyControlStyles('ButtonFace', 'HighlightText', undefined),
          },
        },
        modernUxEnabled && {
          opacity: 0.4,
        },
      ],
      rootCheckedDisabled: {
        background: appNeutralColors.gray40, // #E1DFDD
        borderColor: appNeutralColors.gray40, // #E1DFDD
        selectors: {
          [baseFabricFocusSelector]: applyControlStyles(appNeutralColors.gray40),
          [HighContrastSelector]: applyControlStyles('Highlight', 'HighlightText', undefined),
        },
      },
      iconDisabled: [
        !modernUxEnabled && {
          opacity: 1,
        },
      ],
    };
  }
);

/* Private helpers: split buttons */

/**
 * Split button styles that sets the focused border
 * around the main control and the menu control for WCAG compliance.
 */
const applySplitButtonFocusedStyles = memoizeFunction(
  (): IStyle => {
    return {
      selectors: {
        [appThemedSplitButtonMainControlSelector]: {
          selectors: {
            ':after': mergeStyles(
              getInnerBorderStyles({
                width: `${borderThicknessFocused}px 0px ${borderThicknessFocused}px ${borderThicknessFocused}px`,
                color: borderFocused,
                gap: -1,
              }),
              {
                borderRadius,
                selectors: {
                  [HighContrastSelector]: { borderWidth: 0 },
                },
              }
            ),
          },
        },
        [appThemedSplitButtonMenuControlSelector]: {
          selectors: {
            ':after': mergeStyles(
              getInnerBorderStyles({
                width: `${borderThicknessFocused}px ${borderThicknessFocused}px ${borderThicknessFocused}px 0px`,
                color: borderFocused,
                gap: -1,
              }),
              {
                borderRadius,
                selectors: {
                  [HighContrastSelector]: { borderWidth: 0 },
                },
              }
            ),
          },
        },
      },
    };
  }
);

/**
 * @param backgroundColor color of the control background
 * @param contentColor color of the content rendered inside of the control, includes labels and icons
 * @param borderColor color of the border surrounding the main control
 *
 * @returns a set of styles for the main controls (root) in the split button that uses the provided colors
 */
const applySplitButtonMainControlStyles = memoizeFunction(
  (backgroundColor?: string, contentColor?: string, borderColor?: string): IStyle => {
    const borderRightStyles: IStyle = {
      borderRight: 0,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    };
    return mergeStyles(applyControlStyles(backgroundColor, contentColor, borderColor), {
      selectors: {
        [`&${appThemedSplitButtonMainControlSelector}`]: [
          borderRightStyles,
          {
            selectors: {
              [':hover']: borderRightStyles,
              [':active']: borderRightStyles,
              [':hover:active']: borderRightStyles,
            },
          },
        ],
      },
    });
  }
);

/**
 * @param backgroundColor color of the control background
 * @param contentColor color of the content rendered inside of the control, includes labels and icons
 * @param borderColor color of the border surrounding the main control
 *
 * @returns a set of styles for the menu control in the split button that uses the provided colors
 */
const applySplitButtonMenuControlStyles = memoizeFunction(
  (backgroundColor?: string, contentColor?: string, borderColor?: string): IStyle => {
    return mergeStyles(applyControlStyles(backgroundColor, contentColor, borderColor), {
      borderLeft: 0,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    });
  }
);

/* Split button state combinations: high contrast/forced colors */
const splitButtonHighContrastStyles: IButtonStyles = mergeStyleSets(highContrastStyles, {
  root: [
    appThemedButtonClassNames.splitButtonMainControl,
    {
      selectors: {
        [':active']: {
          selectors: {
            [HighContrastSelector]: applySplitButtonMainControlStyles('Highlight', 'HighlightText', undefined),
          },
        },
      },
    },
  ],
  rootPressed: {
    selectors: {
      [':active']: {
        selectors: {
          [HighContrastSelector]: applySplitButtonMainControlStyles('Highlight', 'HighlightText', undefined),
        },
      },
    },
  },
  rootExpanded: {
    selectors: {
      [':active']: {
        selectors: {
          [HighContrastSelector]: applySplitButtonMainControlStyles('Highlight', 'HighlightText', undefined),
        },
      },
    },
  },
  rootExpandedHovered: [
    {
      selectors: {
        [':active']: {
          selectors: {
            [HighContrastSelector]: applySplitButtonMainControlStyles('Highlight', 'HighlightText', undefined),
          },
        },
      },
    },
  ],
  splitButtonMenuButton: [
    appThemedButtonClassNames.splitButtonMenuControl,
    {
      selectors: {
        [HighContrastSelector]: [
          applySplitButtonMenuControlStyles('ButtonFace', 'ButtonText', 'ButtonText'),
          {
            selectors: {
              // no equivalent to splitButtonMenuButtonHovered
              [':hover']: applySplitButtonMenuControlStyles('Highlight', 'HighlightText', undefined),
              // no equivalent to splitButtonMenuButtonPressed
              [':active']: applySplitButtonMenuControlStyles('Highlight', 'HighlightText', undefined),
            },
          },
        ],
      },
    },
  ],
  splitButtonMenuButtonExpanded: {
    selectors: {
      [HighContrastSelector]: [
        applySplitButtonMenuControlStyles('Highlight', 'HighlightText', undefined),
        {
          selectors: {
            // no equivalent to splitButtonMenuButtonExpandedHovered
            [':hover']: applySplitButtonMenuControlStyles('Highlight', 'HighlightText', undefined),
            // no equivalent to splitButtonMenuButtonExpandedPressed
            [':active']: applySplitButtonMenuControlStyles('Highlight', 'HighlightText', undefined),
          },
        },
      ],
    },
  },
});

/* Split button state combinations: primary */
const primarySplitButtonStyles = memoizeFunction(
  (appColors: AppColors): IButtonStyles => {
    // set app colors to state variables
    appPaletteRest = appColors.primary;
    appPaletteHovered = appColors.shade10;
    appPalettePressed = appColors.shade30;
    appPaletteSelected = appColors.shade20;
    const sharedPrimaryControlStyles: IButtonStyles = primaryControlStyles(appColors);
    return mergeStyleSets(
      sharedPrimaryControlStyles,
      {
        root: [
          appThemedButtonClassNames.splitButtonMainControl,
          applySplitButtonMainControlStyles(appPaletteRest, neutralRest, appPaletteRest),
          {
            position: 'initial',
            selectors: {
              [':active']: applySplitButtonMainControlStyles(appPalettePressed, neutralRest, appPalettePressed),
            },
          },
        ],
        rootHovered: {
          selectors: {
            [':active']: applySplitButtonMainControlStyles(appPalettePressed, neutralRest, appPalettePressed),
          },
        },
        splitButtonContainerFocused: applySplitButtonFocusedStyles(),
        splitButtonMenuButton: [
          appThemedButtonClassNames.splitButtonMenuControl,
          applySplitButtonMenuControlStyles(appPaletteRest, neutralRest, appPaletteRest),
          {
            selectors: {
              // no equivalent to splitButtonMenuButtonHovered
              [':hover']: applySplitButtonMenuControlStyles(appPaletteHovered, neutralRest, appPaletteHovered),
              // no equivalent to splitButtonMenuButtonPressed
              [':active']: applySplitButtonMenuControlStyles(appPalettePressed, neutralRest, appPalettePressed),
            },
          },
        ],
        splitButtonMenuButtonExpanded: [
          applySplitButtonMenuControlStyles(appPaletteSelected, neutralRest, appPaletteSelected),
          {
            selectors: {
              // no equivalent to splitButtonMenuButtonExpandedHovered
              [':hover']: applySplitButtonMenuControlStyles(appPaletteHovered, neutralRest, appPaletteHovered),
              // no equivalent to splitButtonMenuButtonExpandedPressed
              [':active']: applySplitButtonMenuControlStyles(appPalettePressed, neutralRest, appPalettePressed),
            },
          },
        ],
      },
      splitButtonHighContrastStyles
    );
  }
);

/* Split button state combinations: inverted */
const invertedSplitButtonStyles = memoizeFunction(
  (appColors: AppColors): IButtonStyles => {
    // set app colors to state variables
    const modernUxEnabled = getModernUxExperimentPreference();
    if (modernUxEnabled) {
      appPaletteRest = appColors.primary;
      appPaletteHovered = appColors.primary;
      appPalettePressed = appColors.primary;
      appPaletteSelected = appColors.primary;
    } else {
      appPaletteRest = appColors.primary;
      appPaletteHovered = appColors.shade10;
      appPalettePressed = appColors.shade30;
      appPaletteSelected = appColors.shade20;
    }
    const sharedInvertedControlStyles: IButtonStyles = invertedControlStyles(appColors);
    return mergeStyleSets(
      sharedInvertedControlStyles,
      {
        root: [
          appThemedButtonClassNames.splitButtonMainControl,
          applySplitButtonMainControlStyles(neutralRest, appPaletteRest, borderRest),
          {
            position: 'initial',
            selectors: {
              [':active']: applySplitButtonMainControlStyles(neutralPressed, appPalettePressed, borderPressed),
            },
          },
        ],
        rootHovered: {
          selectors: {
            [':active']: applySplitButtonMainControlStyles(neutralPressed, appPalettePressed, borderPressed),
          },
        },
        splitButtonContainerFocused: applySplitButtonFocusedStyles(),
        splitButtonMenuButton: [
          appThemedButtonClassNames.splitButtonMenuControl,
          applySplitButtonMenuControlStyles(neutralRest, appPaletteRest, borderRest),
          {
            selectors: {
              // no equivalent to splitButtonMenuButtonHovered
              [':hover']: applySplitButtonMenuControlStyles(neutralHovered, appPaletteHovered, borderHovered),
              // no equivalent to splitButtonMenuButtonPressed
              [':active']: applySplitButtonMenuControlStyles(neutralPressed, appPalettePressed, borderPressed),
            },
          },
        ],
        splitButtonMenuButtonExpanded: [
          applySplitButtonMenuControlStyles(neutralSelected, appPaletteRest, borderSelected),
          {
            selectors: {
              // no equivalent to splitButtonMenuButtonExpandedHovered
              [':hover']: applySplitButtonMenuControlStyles(neutralHovered, appPaletteHovered, borderHovered),
              // no equivalent to splitButtonMenuButtonExpandedPressed
              [':active']: applySplitButtonMenuControlStyles(neutralPressed, appPalettePressed, borderPressed),
            },
          },
        ],
      },
      splitButtonHighContrastStyles
    );
  }
);

/* Split button state combinations: disabled */
const getDisabledSplitButtonStyles = memoizeFunction(
  (appColors: AppColors): IButtonStyles => {
    const modernUxEnabled = getModernUxExperimentPreference();
    const textColor = modernUxEnabled ? appColors.primary : neutralDisabledContent;
    const backgroundColor = modernUxEnabled ? appNeutralColors.white : neutralDisabled;
    const borderColor = modernUxEnabled ? '#D1D1D1' : borderDisabled;
    return mergeStyleSets(getDisabledControlStyles(appColors), {
      rootDisabled: [appThemedButtonClassNames.splitButtonMainControl, { position: 'initial' }],
      // split button
      splitButtonContainer: {
        selectors: {
          // on disabled state, the Fluent splitButtonContainerFocused
          // selector is not available
          [':focus']: applySplitButtonFocusedStyles(),
        },
      },
      splitButtonMenuButton: appThemedButtonClassNames.splitButtonMenuControl,
      splitButtonMenuButtonDisabled: applySplitButtonMenuControlStyles(backgroundColor, textColor, borderColor),
    });
  }
);
